// @ts-nocheck
import { addNode, removeNode, useDispatch, useSelector } from "@ggs/store"
import { Box } from "@mui/material"
import { graphql, Slice } from "gatsby"
import { find, get } from "lodash"
import { useEffect, useState } from "react"
import { type ResponsiveImageType } from ".."
import { LocationBanners, TripDestinationSearch } from "../components"
import SEOHead from "../components/head"
import Layout from "../components/layout"
import {
  mapArticleTeaser,
  mapCountryData,
  mapSectionSwitcher,
  mapWishlistData,
} from "../helpers/data-mappers"
import { useGeoPagesSolrData, useTripsData } from "../local/hooks"
import useGeoData from "../local/hooks/useGeoData"
import useGlobalAdsSolrData from "../local/hooks/useGlobalAdsSolrData"
import useStaffData from "../local/hooks/useStaffData"
// country page components
import {
  ContactCTA,
  ContentHighlight,
  DynamicSubNav,
  ExploreList,
  Facts,
  GlobalAdBanners,
  Hero,
  SectionSwitcher,
  SuggestedTrips,
  Teaser,
  VisualMediaContent,
} from "../components"
import useArticlesSolrData from "../local/hooks/useArticlesSolrData"

export type GeoEntityItems = {
  label: string
  description: string
  type: string
  lat: number
  lng: number
  uri: string
  image: ResponsiveImageType
}

export type GeoEntityProps = {
  items: GeoEntityItems[]
}

export default function CountryView(props) {
  const country = get(props, "data.nodeCountry", {})
  const dispatch = useDispatch()
  const wishlist = useSelector((state) => state.views.wishlist)
  const likedArticles = wishlist?.article && wishlist?.article.map((article) => article.nid)
  const likedTrips = wishlist?.trip && wishlist?.trip.map((trip) => trip.nid)
  const { getCountryGenericGlobalAds, getDestinationGenericGlobalAds } = useGlobalAdsSolrData()
  const { getStaffByDestination } = useStaffData()
  const { getGeoData } = useGeoData()
  const { getTripsByCountryAndDestination } = useTripsData()
  const { getDynamicSubNav, getTravelInformationPages, getTripIdeaPages } = useGeoPagesSolrData()
  const [globalAds, setGlobalAds] = useState(null)
  const [contactCtaStaff, setContactCtaStaff] = useState(null)
  const [articleTeasers, setArticleTeasers] = useState(null)
  const [suggestedTrips, setSuggestedTrips] = useState(null)
  const [subNav, setSubNav] = useState(null)
  const [travelInformationPages, setTravelInformationPages] = useState(null)
  const [tripIdeaPages, setTripIdeaPages] = useState(null)
  const refCountryId = country?.external_id || ""
  const refDestinationId = get(country, "destinations.[0].external_id", "")
  const tripItineraryUrl = find(subNav, (navLink) => navLink.title === "Trip Itineraries")?.uri
  const { getArticles } = useArticlesSolrData()
  const userCountry = useSelector((state) => state.settings.country)

  const resolveGlobalAds = async () => {
    const countryAds = await getCountryGenericGlobalAds(refCountryId)
    const destinationsAds = await getDestinationGenericGlobalAds(refDestinationId)
    // Combine and filter unique ads based on id
    const uniqueAds = [
      ...new Map([...countryAds, ...destinationsAds].map((ad) => [ad.id, ad])).values(),
    ]
    setGlobalAds(uniqueAds)
  }

  const mappedData = mapCountryData(country)

  const {
    hero,
    external_id,
    destinationId,
    content_highlight,
    vmedia_content,
    facts_overview,
    contact_cta,
    explore_list,
    teaser_3up,
    suggested_trips,
    slug,
    section_switcher,
  } = mappedData

  // Resolve Solr data.
  useEffect(() => {
    resolveGlobalAds().then(() => console.log("Resolving global ads"))
    if (refDestinationId) {
      getStaffByDestination(refDestinationId).then((data) => {
        setContactCtaStaff(data)
      })
    }
    getArticles(teaser_3up, external_id, refDestinationId).then((data) => {
      const filteredData = mapWishlistData(likedArticles, data)
      setArticleTeasers(filteredData)
    })
    if (country?.external_id) {
      getDynamicSubNav(country.external_id).then((data) => {
        const overviewLink = { title: `${country?.title} Overview`, uri: null }
        const composedLinks = data ? [overviewLink].concat(data) : [overviewLink]
        setSubNav(composedLinks)
      })
      // get trip idea links
      getTripIdeaPages(country.external_id).then((data) => {
        setTripIdeaPages(data)
      })
      // get travel information links
      getTravelInformationPages(country.external_id).then((data) => {
        setTravelInformationPages(data)
      })
    }
  }, [])

  useEffect(() => {
    getTripsByCountryAndDestination(refDestinationId, refCountryId, country?.suggested_trips).then(
      (data) => {
        const filteredData = mapWishlistData(likedTrips, data)
        setSuggestedTrips({
          title: suggested_trips?.title || "",
          subtitle: suggested_trips?.subtitle || "",
          items: filteredData,
        })
      }
    )
  }, [userCountry])

  const addToWishList = (selectedEntity) => {
    if (selectedEntity.contentType === "article") {
      const filteredState = mapArticleTeaser(articleTeasers, selectedEntity, true)
      setArticleTeasers(filteredState)
    }
    if (selectedEntity.contentType === "trip") {
      const filteredState = mapArticleTeaser(suggestedTrips.items, selectedEntity, true)
      setSuggestedTrips({
        ...suggestedTrips,
        items: filteredState,
      })
    }
    dispatch(
      addNode({ type: selectedEntity.contentType, entity: selectedEntity, idPropertyKey: "nid" })
    )
  }

  const removeFromWishList = (selectedEntity) => {
    if (selectedEntity.contentType === "article") {
      const filteredState = mapArticleTeaser(articleTeasers, selectedEntity, false)
      setArticleTeasers(filteredState)
    }
    if (selectedEntity.contentType === "trip") {
      const filteredState = mapArticleTeaser(suggestedTrips.items, selectedEntity, false)
      setSuggestedTrips({
        ...suggestedTrips,
        items: filteredState,
      })
    }
    dispatch(removeNode({ type: selectedEntity.contentType, findPredicate: selectedEntity }))
  }

  if (!country) {
    return <Box>{t("Country not found")}</Box>
  }

  const travelInformation = [
    {
      title: "Culture and Traditions",
      uri: "/culture-and-traditions",
    },
    {
      title: "Best Time to Visit",
      uri: "/",
    },
    {
      title: "Things to Know",
      uri: "/",
    },
  ]

  return (
    <>
      {ComposeHead(props)}
      <Layout
        hero={
          <Hero
            title={hero?.title}
            subtitle={hero?.subtitle}
            media={hero?.images}
            location={hero?.location}
            subtitleImageUrl={hero?.subtitleImageUrl}
            video={hero?.video}
            path={hero?.breadcrumbs}
            cta={subNav?.[2]}
          />
        }
      >
        {subNav && (
          <DynamicSubNav
            links={subNav}
            travelInformation={travelInformationPages}
            tripIdea={tripIdeaPages}
            countryId={refCountryId}
            activeLink={`${country?.title} Overview`}
          />
        )}
        {facts_overview && (
          <Facts
            title={facts_overview?.title}
            subtitle={facts_overview?.subtitle}
            description={facts_overview?.description}
            items={facts_overview?.items}
          />
        )}
        <Box className="trip-search-box" sx={{ display: "none" }}>
          <TripDestinationSearch
            title={`Vacations, Tours, & Travel Packages`}
            location={refCountryId}
            path={tripItineraryUrl}
          />
        </Box>
        {suggestedTrips?.items?.length > 0 && (
          <SuggestedTrips
            {...suggestedTrips}
            link={{ title: "All Trips", uri: tripItineraryUrl || "/trips" }}
            onLike={addToWishList}
            onUnlike={removeFromWishList}
          />
        )}
        {vmedia_content && <VisualMediaContent images={vmedia_content?.images} />}
        {explore_list && (
          <ExploreList
            title={explore_list?.title}
            subtitle={explore_list?.subtitle}
            description={explore_list?.description}
            items={explore_list?.items}
            link={{ title: "Show All Places To Go", uri: `${slug}/places-to-go` }}
          />
        )}
        {globalAds && globalAds.length > 0 && (
          <Box pt={{ xs: 5, md: 10 }}>
            <LocationBanners items={globalAds} />
          </Box>
        )}
        {content_highlight && (
          <ContentHighlight
            color={content_highlight?.color}
            title={content_highlight?.title}
            subtitle={content_highlight?.subtitle}
            description={content_highlight?.description}
            image={content_highlight?.image}
            link={content_highlight?.link}
          />
        )}
        {section_switcher?.items && (
          <SectionSwitcher items={mapSectionSwitcher(section_switcher?.items)} />
        )}
        {teaser_3up && (
          <Teaser
            title={teaser_3up?.title}
            subtitle={teaser_3up?.subtitle}
            link={teaser_3up?.link}
            items={articleTeasers}
            onLike={addToWishList}
            onUnlike={removeFromWishList}
          />
        )}
        <ContactCTA
          title={contact_cta?.title}
          subtitle={contact_cta?.subtitle}
          description={contact_cta?.description}
          link={contact_cta?.link}
          image={contact_cta?.image}
          staff={contactCtaStaff}
        />
        <Slice alias="preFooterStyle2" destinationId={destinationId} />
      </Layout>
    </>
  )
}

export const ComposeHead = (props) => {
  const { nodeCountry } = props.data
  const metatag = get(nodeCountry, "metatag.items")
  const url = get(nodeCountry, "slug")
  const image = get(nodeCountry, "hero.images[0].variants[0].url")
  return <SEOHead metas={metatag} url={url} image={image} />
}

export const query = graphql`
  query CountryTemplateQuery($id: String!) {
    nodeCountry(id: { eq: $id }) {
      id
      external_id
      slug
      title
      metatag {
        items {
          content
          name
        }
      }
      destinations {
        title
        external_id
        slug
      }
      hero {
        ...HeroFragment
      }
      content_highlight {
        ...ContentHighlightFragment
      }
      vmedia_content {
        ...VisualMediaContentFragment
      }
      contact_cta {
        ...ContactCtaFragment
      }
      facts_overview {
        ...FactsOverviewFragment
      }
      explore_list {
        ...ExploreListFragment
      }
      teaser_3up {
        link {
          title
          uri
        }
        ...Teaser3UpFragment
      }
      suggested_trips {
        ...SuggestedTripsFragment
      }
      section_switcher {
        ...SectionSwitcherFragment
      }
    }
  }
`
